import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdSettings, MdPayment, MdNotifications, MdPalette, MdImage, MdCheck, MdDescription, MdInfo } from 'react-icons/md';
import { configuracionAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';

const SettingsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const SettingsLayout = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const SettingsSidebar = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
  height: fit-content;
`;

const SidebarItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.active ? props.theme.colors.primary + '15' : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textPrimary};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-align: left;
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  margin-bottom: ${props => props.theme.spacing.sm};

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primary + '15' : props.theme.colors.background};
  }
`;

const SettingsContent = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const SectionDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.xl} 0;
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LogoPreview = styled.div`
  width: 120px;
  height: 120px;
  background-color: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.theme.colors.border};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: ${props => props.theme.spacing.sm};
  }
  
  svg {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 3rem;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const LogoInfo = styled.div`
  flex: 1;
`;

const LogoTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const LogoDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
`;

const UploadButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SaveButton = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    background-color: ${props => props.theme.colors.textLight};
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  border: 1px solid #f5c6cb;
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }
`;

const SubsectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: ${props => props.theme.colors.primary};
`;

const CheckboxLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  user-select: none;
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-family: inherit;
  resize: vertical;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ColorPickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ColorPreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const ColorPreview = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.color};
  border: 2px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ColorInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-family: 'Courier New', monospace;
  transition: ${props => props.theme.transitions.fast};
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }
`;

const ColorPaletteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
`;

const ColorOption = styled.button`
  width: 45px;
  height: 45px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.color};
  border: 2px solid ${props => props.selected ? props.theme.colors.textPrimary : props.theme.colors.border};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  box-shadow: ${props => props.theme.shadows.sm};
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows.md};
  }

  ${props => props.selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 20px;
      font-weight: bold;
      text-shadow: 0 0 3px rgba(0,0,0,0.5);
    }
  `}
`;

const ColorLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const PreviewNote = styled.div`
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  svg {
    flex-shrink: 0;
  }
`;

const Settings = () => {
  const { themeName, setTheme, primaryColor, setPrimaryColor } = useTheme();
  const [activeSection, setActiveSection] = useState('general');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = React.useRef(null);
  
  // Formulario de configuración
  const [formData, setFormData] = useState({
    nombre_empresa: '',
    direccion: '',
    email_contacto: '',
    telefono: '',
    logo_url: '',
    moneda: '',
    dias_gracia: 7,
    prefijo_factura: '',
    iva: 0,
    email_notificaciones: '',
    recordatorio_dias: 3,
    frecuencia_reportes: '',
    tema: '',
    color_principal: '',
    idioma: '',
    // Campos de plantillas de recibo
    plantilla_recibo_mostrar_logo: true,
    plantilla_recibo_mostrar_direccion: true,
    plantilla_recibo_mostrar_telefono: true,
    plantilla_recibo_mostrar_email: true,
    plantilla_recibo_pie_pagina: '',
    plantilla_recibo_notas_adicionales: '',
    plantilla_recibo_terminos: '',
    // Campos de plantillas de comprobante
    plantilla_comprobante_mostrar_logo: true,
    plantilla_comprobante_mostrar_firma: false,
    plantilla_comprobante_pie_pagina: '',
    plantilla_comprobante_mensaje_agradecimiento: 'Gracias por su pago',
    // Formatos
    formato_fecha: 'DD/MM/YYYY',
    formato_moneda: '$0,0.00',
    mostrar_sello_digital: false,
    texto_sello_digital: ''
  });

  // Cargar configuración al montar el componente
  useEffect(() => {
    loadConfiguracion();
  }, []);

  const loadConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await configuracionAPI.getConfiguracion();
      const configData = response.data.data;
      setConfig(configData);
      setFormData(configData);
      
      // Sincronizar tema si existe en la configuración
      if (configData.tema) {
        setTheme(configData.tema);
      }
      
      // Sincronizar color principal SOLO al cargar inicialmente
      // (no cuando el usuario está editando)
      if (configData.color_principal && !formData.color_principal) {
        setPrimaryColor(configData.color_principal);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      setErrorMessage('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleColorChange = (newColor) => {
    // Validar formato hex
    const hexRegex = /^#[0-9A-F]{6}$/i;
    if (hexRegex.test(newColor)) {
      setFormData(prev => ({
        ...prev,
        color_principal: newColor
      }));
      // NO aplicar el color inmediatamente, solo actualizar el formulario
    }
  };

  const handleColorInputChange = (e) => {
    let value = e.target.value.toUpperCase();
    
    // Asegurar que comience con #
    if (!value.startsWith('#')) {
      value = '#' + value;
    }
    
    // Limitar a 7 caracteres (#RRGGBB)
    value = value.slice(0, 7);
    
    setFormData(prev => ({
      ...prev,
      color_principal: value
    }));
    
    // NO aplicar el color, solo actualizar el formulario
  };

  const presetColors = [
    '#5B9BD5', // Azul (default)
    '#2196F3', // Azul Material
    '#4CAF50', // Verde
    '#FF9800', // Naranja
    '#F44336', // Rojo
    '#9C27B0', // Púrpura
    '#00BCD4', // Cyan
    '#FF5722', // Rojo profundo
    '#795548', // Marrón
    '#607D8B', // Gris azulado
    '#E91E63', // Rosa
    '#3F51B5', // Índigo
  ];

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrorMessage('Por favor selecciona una imagen válida (PNG, JPG, GIF, WEBP)');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage('La imagen debe ser menor a 2MB');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    // Convertir a base64 para preview y guardado
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData(prev => ({
        ...prev,
        logo_url: base64String
      }));
    };
    reader.onerror = () => {
      setErrorMessage('Error al cargar la imagen');
      setTimeout(() => setErrorMessage(''), 5000);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await configuracionAPI.updateConfiguracion(formData);
      
      // Aplicar el color principal DESPUÉS de guardar exitosamente
      if (formData.color_principal) {
        const hexRegex = /^#[0-9A-F]{6}$/i;
        if (hexRegex.test(formData.color_principal)) {
          setPrimaryColor(formData.color_principal);
        }
      }
      
      setSuccessMessage('Configuración guardada exitosamente');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Recargar configuración
      loadConfiguracion();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setErrorMessage(error.message || 'Error al guardar la configuración');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SettingsContainer>
        <PageHeader>
          <Title>Configuración</Title>
        </PageHeader>
        <div>Cargando configuración...</div>
      </SettingsContainer>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <SectionTitle>Información General de la Empresa</SectionTitle>
            <SectionDescription>
              Actualiza los datos de tu empresa y personaliza el logotipo.
            </SectionDescription>

            {successMessage && (
              <SuccessMessage>
                <MdCheck /> {successMessage}
              </SuccessMessage>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <FormSection>
              <LogoSection>
                <LogoPreview>
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo de la empresa" />
                  ) : (
                    <MdImage />
                  )}
                </LogoPreview>
                <LogoInfo>
                  <LogoTitle>Logotipo de la Empresa</LogoTitle>
                  <LogoDescription>
                    Sube una imagen de tu logo (PNG, JPG, GIF, WEBP - máx. 2MB).
                  </LogoDescription>
                  <UploadButton onClick={handleLogoClick}>
                    {formData.logo_url ? 'Cambiar Logo' : 'Subir Logo'}
                  </UploadButton>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleLogoChange}
                  />
                </LogoInfo>
              </LogoSection>

              <FormGrid>
                <FormGroup>
                  <Label>Nombre de la Empresa</Label>
                  <Input 
                    type="text"
                    name="nombre_empresa"
                    value={formData.nombre_empresa}
                    onChange={handleInputChange}
                    placeholder="Nombre de tu empresa"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Dirección</Label>
                  <Input 
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleInputChange}
                    placeholder="Dirección completa"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Correo Electrónico de Contacto</Label>
                  <Input 
                    type="email"
                    name="email_contacto"
                    value={formData.email_contacto}
                    onChange={handleInputChange}
                    placeholder="email@empresa.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Número de Teléfono</Label>
                  <Input 
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="+1 234 567 8900"
                  />
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'payments':
        return (
          <>
            <SectionTitle>Pagos y Recibos</SectionTitle>
            <SectionDescription>
              Configura las opciones de pago y personaliza tus recibos.
            </SectionDescription>

            {successMessage && (
              <SuccessMessage>
                <MdCheck /> {successMessage}
              </SuccessMessage>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <FormSection>
              <FormGrid>
                <FormGroup>
                  <Label>Moneda Predeterminada</Label>
                  <Select
                    name="moneda"
                    value={formData.moneda}
                    onChange={handleInputChange}
                  >
                    <option value="MXN ($)">MXN ($)</option>
                    <option value="USD ($)">USD ($)</option>
                    <option value="EUR (€)">EUR (€)</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Días de Gracia para Pagos</Label>
                  <Input 
                    type="number"
                    name="dias_gracia"
                    value={formData.dias_gracia}
                    onChange={handleInputChange}
                    placeholder="7"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Prefijo de Factura</Label>
                  <Input 
                    type="text"
                    name="prefijo_factura"
                    value={formData.prefijo_factura}
                    onChange={handleInputChange}
                    placeholder="INV-"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>IVA (%)</Label>
                  <Input 
                    type="number"
                    name="iva"
                    value={formData.iva}
                    onChange={handleInputChange}
                    placeholder="0"
                    step="0.01"
                  />
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'notifications':
        return (
          <>
            <SectionTitle>Notificaciones</SectionTitle>
            <SectionDescription>
              Configura cómo y cuándo recibir notificaciones.
            </SectionDescription>

            {successMessage && (
              <SuccessMessage>
                <MdCheck /> {successMessage}
              </SuccessMessage>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <FormSection>
              <FormGrid>
                <FormGroup>
                  <Label>Email de Notificaciones</Label>
                  <Input 
                    type="email"
                    name="email_notificaciones"
                    value={formData.email_notificaciones}
                    onChange={handleInputChange}
                    placeholder="email@empresa.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Recordatorio de Pago (días antes)</Label>
                  <Input 
                    type="number"
                    name="recordatorio_dias"
                    value={formData.recordatorio_dias}
                    onChange={handleInputChange}
                    placeholder="3"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Frecuencia de Reportes</Label>
                  <Select
                    name="frecuencia_reportes"
                    value={formData.frecuencia_reportes}
                    onChange={handleInputChange}
                  >
                    <option value="Diario">Diario</option>
                    <option value="Semanal">Semanal</option>
                    <option value="Mensual">Mensual</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'appearance':
        return (
          <>
            <SectionTitle>Apariencia</SectionTitle>
            <SectionDescription>
              Personaliza la apariencia de la aplicación.
            </SectionDescription>

            {successMessage && (
              <SuccessMessage>
                <MdCheck /> {successMessage}
              </SuccessMessage>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <FormSection>
              <FormGrid>
                <FormGroup>
                  <Label>Tema</Label>
                  <Select
                    name="tema"
                    value={themeName}
                    onChange={(e) => {
                      const newTheme = e.target.value;
                      setTheme(newTheme);
                      setFormData(prev => ({ ...prev, tema: newTheme }));
                    }}
                  >
                    <option value="Claro">Claro</option>
                    <option value="Oscuro">Oscuro</option>
                    <option value="Auto">Automático</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Color Principal</Label>
                  <ColorPickerContainer>
                    <ColorPreviewWrapper>
                      <ColorPreview color={formData.color_principal || '#5B9BD5'} />
                      <ColorInput 
                        type="text"
                        name="color_principal"
                        value={formData.color_principal || '#5B9BD5'}
                        onChange={handleColorInputChange}
                        placeholder="#5B9BD5"
                        maxLength={7}
                      />
                    </ColorPreviewWrapper>
                    <ColorLabel>Colores predefinidos</ColorLabel>
                    <ColorPaletteGrid>
                      {presetColors.map(color => (
                        <ColorOption 
                          key={color}
                          color={color}
                          selected={formData.color_principal === color}
                          onClick={(e) => {
                            e.preventDefault();
                            handleColorChange(color);
                          }}
                          title={color}
                        />
                      ))}
                    </ColorPaletteGrid>
                    <PreviewNote>
                      <MdInfo size={18} />
                      <span>El color se aplicará cuando guardes los cambios</span>
                    </PreviewNote>
                  </ColorPickerContainer>
                </FormGroup>

                <FormGroup>
                  <Label>Idioma</Label>
                  <Select
                    name="idioma"
                    value={formData.idioma}
                    onChange={handleInputChange}
                  >
                    <option value="Español">Español</option>
                    <option value="English">English</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'templates':
        return (
          <>
            <SectionTitle>Personalización de Plantillas</SectionTitle>
            <SectionDescription>
              Personaliza cómo se verán los recibos y comprobantes de pago.
            </SectionDescription>

            {successMessage && (
              <SuccessMessage>
                <MdCheck /> {successMessage}
              </SuccessMessage>
            )}
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            {/* Plantilla de Recibos */}
            <FormSection>
              <SubsectionTitle>📄 Plantilla de Recibos</SubsectionTitle>
              
              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_logo"
                    name="plantilla_recibo_mostrar_logo"
                    checked={formData.plantilla_recibo_mostrar_logo}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_logo">
                    Mostrar logo de la empresa
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_direccion"
                    name="plantilla_recibo_mostrar_direccion"
                    checked={formData.plantilla_recibo_mostrar_direccion}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_direccion">
                    Mostrar dirección de la empresa
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_telefono"
                    name="plantilla_recibo_mostrar_telefono"
                    checked={formData.plantilla_recibo_mostrar_telefono}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_telefono">
                    Mostrar teléfono de contacto
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_email"
                    name="plantilla_recibo_mostrar_email"
                    checked={formData.plantilla_recibo_mostrar_email}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_email">
                    Mostrar email de contacto
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <Label>Notas Adicionales (aparecen en el recibo)</Label>
                <TextArea
                  name="plantilla_recibo_notas_adicionales"
                  value={formData.plantilla_recibo_notas_adicionales || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Favor de realizar el pago antes de la fecha de vencimiento."
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <Label>Términos y Condiciones</Label>
                <TextArea
                  name="plantilla_recibo_terminos"
                  value={formData.plantilla_recibo_terminos || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Los pagos vencidos generan intereses moratorios..."
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <Label>Pie de Página</Label>
                <TextArea
                  name="plantilla_recibo_pie_pagina"
                  value={formData.plantilla_recibo_pie_pagina || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Este documento es un comprobante de la deuda pendiente."
                  rows="2"
                />
              </FormGroup>
            </FormSection>

            {/* Plantilla de Comprobantes */}
            <FormSection style={{ marginTop: '30px' }}>
              <SubsectionTitle>🧾 Plantilla de Comprobantes de Pago</SubsectionTitle>
              
              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_comprobante_mostrar_logo"
                    name="plantilla_comprobante_mostrar_logo"
                    checked={formData.plantilla_comprobante_mostrar_logo}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_comprobante_mostrar_logo">
                    Mostrar logo de la empresa
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_comprobante_mostrar_firma"
                    name="plantilla_comprobante_mostrar_firma"
                    checked={formData.plantilla_comprobante_mostrar_firma}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_comprobante_mostrar_firma">
                    Mostrar espacio para firma
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <Label>Mensaje de Agradecimiento</Label>
                <Input
                  type="text"
                  name="plantilla_comprobante_mensaje_agradecimiento"
                  value={formData.plantilla_comprobante_mensaje_agradecimiento || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Gracias por su pago"
                />
              </FormGroup>

              <FormGroup>
                <Label>Pie de Página del Comprobante</Label>
                <TextArea
                  name="plantilla_comprobante_pie_pagina"
                  value={formData.plantilla_comprobante_pie_pagina || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Para cualquier aclaración, contacte con nosotros."
                  rows="2"
                />
              </FormGroup>
            </FormSection>

            {/* Formatos */}
            <FormSection style={{ marginTop: '30px' }}>
              <SubsectionTitle>⚙️ Formatos</SubsectionTitle>
              
              <FormGrid>
                <FormGroup>
                  <Label>Formato de Fecha</Label>
                  <Select
                    name="formato_fecha"
                    value={formData.formato_fecha}
                    onChange={handleInputChange}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2025)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Formato de Moneda</Label>
                  <Select
                    name="formato_moneda"
                    value={formData.formato_moneda}
                    onChange={handleInputChange}
                  >
                    <option value="$0,0.00">$1,234.56</option>
                    <option value="$0.0,00">$1.234,56</option>
                    <option value="0,0.00">1,234.56</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="mostrar_sello_digital"
                    name="mostrar_sello_digital"
                    checked={formData.mostrar_sello_digital}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="mostrar_sello_digital">
                    Mostrar sello digital/código QR
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              {formData.mostrar_sello_digital && (
                <FormGroup>
                  <Label>Texto para el Sello Digital</Label>
                  <Input
                    type="text"
                    name="texto_sello_digital"
                    value={formData.texto_sello_digital || ''}
                    onChange={handleInputChange}
                    placeholder="Ej: Documento verificado digitalmente"
                  />
                </FormGroup>
              )}

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <SettingsContainer>
      <PageHeader>
        <Title>Configuración</Title>
      </PageHeader>

      <SettingsLayout>
        <SettingsSidebar>
          <SidebarItem 
            active={activeSection === 'general'} 
            onClick={() => setActiveSection('general')}
          >
            <MdSettings />
            <span>General</span>
          </SidebarItem>

          <SidebarItem 
            active={activeSection === 'payments'} 
            onClick={() => setActiveSection('payments')}
          >
            <MdPayment />
            <span>Pagos y Recibos</span>
          </SidebarItem>

          <SidebarItem 
            active={activeSection === 'notifications'} 
            onClick={() => setActiveSection('notifications')}
          >
            <MdNotifications />
            <span>Notificaciones</span>
          </SidebarItem>

          <SidebarItem 
            active={activeSection === 'appearance'} 
            onClick={() => setActiveSection('appearance')}
          >
            <MdPalette />
            <span>Apariencia</span>
          </SidebarItem>

          <SidebarItem 
            active={activeSection === 'templates'} 
            onClick={() => setActiveSection('templates')}
          >
            <MdDescription />
            <span>Plantillas</span>
          </SidebarItem>
        </SettingsSidebar>

        <SettingsContent>
          {renderContent()}
        </SettingsContent>
      </SettingsLayout>
    </SettingsContainer>
  );
};

export default Settings;
